<script setup>
/**
 * H3 插队现状 —— 只读监控页,不做任何操作。
 *
 * H3 只有一个后端、任务严格串行,结局视频和意图视频按需生成两条付费路径靠
 * vg_h3_priority_admissions 抢插队资格(见 packages/video/priority-admission.js),
 * 这张表和 H3 自己的真实排队深度(GET /v1/backends)从来没有地方能看到,这页补上。
 * 不跨剧本包,所以不用 ScenarioSelect。
 */
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, EmptyState, PageHeader, Section, Spinner, Table, useToast } from '@drama/ui'
import { RefreshCw } from '@lucide/vue'

import { getH3QueueStatus } from '../../api/intent-videos.js'
import { formatDate } from './format.js'

const { t } = useI18n()
const toast = useToast()

const loading = ref(false)
const data = ref(null)

const recentColumns = [
  { key: 'job_source', label: t('visualGameAdmin.intentVideo.h3Queue.jobSource') },
  { key: 'job_ref_id', label: t('visualGameAdmin.intentVideo.h3Queue.jobRefId') },
  { key: 'status', label: t('visualGameAdmin.intentVideo.h3Queue.status') },
  { key: 'created_at', label: t('visualGameAdmin.intentVideo.h3Queue.createdAt') },
  { key: 'updated_at', label: t('visualGameAdmin.intentVideo.h3Queue.updatedAt') },
]

async function load() {
  loading.value = true
  try {
    data.value = await getH3QueueStatus()
  } catch (error) {
    toast.error(error.message, { duration: 0 })
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="h3q vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/H3QueuePage.vue">
    <PageHeader :title="t('visualGameAdmin.intentVideo.tabs.h3Queue')">
      <template #subtitle>
        <span>{{ t('visualGameAdmin.intentVideo.h3Queue.subtitle') }}</span>
      </template>
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" /> {{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <Spinner v-if="loading && data === null" />

    <template v-else-if="data !== null">
      <Section :title="t('visualGameAdmin.intentVideo.h3Queue.currentHolder')">
        <EmptyState v-if="data.admission === null" :title="t('visualGameAdmin.intentVideo.h3Queue.noHolder')" />
        <div v-else class="h3q__holder">
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.jobSource') }}:</strong> {{ data.admission.job_source }}</span>
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.jobRefId') }}:</strong> {{ data.admission.job_ref_id }}</span>
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.createdAt') }}:</strong> {{ formatDate(data.admission.created_at) }}</span>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.intentVideo.h3Queue.backend')">
        <p v-if="data.backend_error !== null" class="h3q__error">{{ data.backend_error }}</p>
        <div v-else-if="data.backend !== null" class="h3q__backend">
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.queued') }}:</strong> {{ data.backend.queued }}</span>
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.running') }}:</strong> {{ data.backend.running }}</span>
          <span><strong>{{ t('visualGameAdmin.intentVideo.h3Queue.ready') }}:</strong> {{ data.backend.ready }}/{{ data.backend.total }}</span>
          <Badge :variant="data.backend.status === 'ok' ? 'success' : 'danger'" size="sm">{{ data.backend.status }}</Badge>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.intentVideo.h3Queue.recent')">
        <Table :columns="recentColumns" :rows="data.recent" row-key="id">
          <template #job_source="{ row }">{{ t(`visualGameAdmin.intentVideo.h3Queue.source.${row.job_source}`) }}</template>
          <template #status="{ row }">
            <Badge :variant="row.status === 'holding' ? 'warning' : 'neutral'" size="sm">{{ row.status }}</Badge>
          </template>
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #updated_at="{ row }">{{ formatDate(row.updated_at) }}</template>
        </Table>
      </Section>
    </template>
  </div>
</template>

<style scoped>
.h3q__holder, .h3q__backend { display: flex; gap: var(--drama-space-lg); align-items: center; flex-wrap: wrap; }
.h3q__error { color: var(--drama-color-danger); }
</style>
